import { z } from "zod";
import { TEST_CALL_STATUS, TEST_RESULT } from "@/isomorphic/constants/agent-consts";
import { DEFAULT_LANGUAGES } from "@/isomorphic/constants/languages";

// ============================================================================
// AGENT TEST CALL SCHEMAS (camelCase for API)
// ============================================================================

// Re-export constants for convenience
export { TEST_CALL_STATUS, TEST_RESULT };

// Zod schemas from constants
export const TestCallStatusSchema = z.enum(TEST_CALL_STATUS);
export const TestResultSchema = z.enum(TEST_RESULT);

// ============================================================================
// BASE AGENT TEST CALL SCHEMA (matches backend DbAgentTestCallOutput)
// ============================================================================

// To sync with DbAgentTestCallOutput in backend/schemas/agent-test-call.ts
const BaseAgentTestCallSchema = z.object({
  // Primary fields
  id: z.string(),

  // Relations
  agentId: z.string(),
  agentVersionId: z.string().nullable().optional(),
  phaseId: z.string(),
  userId: z.string(),
  language: z.enum(DEFAULT_LANGUAGES),

  // Timestamps
  createdAt: z.string(),
  updatedAt: z.string(),
  startedAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),

  // Data fields
  callStatus: TestCallStatusSchema.nullable().optional(),
  testResult: TestResultSchema.nullable().optional(),
  testerPhone: z.string().nullable().optional(),
  testDynamicVariables: z.record(z.string(), z.unknown()).nullable().optional(),
  patientAuthData: z.record(z.string(), z.unknown()).nullable().optional(),
  patientAuthAttempts: z.record(z.string(), z.unknown()).nullable().optional(),
  callId: z.string().nullable().optional(),
  callDuration: z.number().nullable().optional(),
  transcript: z.string().nullable().optional(), // Renvoyé undefined par PostgreSQL
  testerNotes: z.string().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  recordingUrl: z.string().nullable().optional(),
  disconnectionReason: z.string().nullable().optional(),
  transcriptObject: z.array(z.record(z.string(), z.unknown())).nullable().optional(),
  retellRawData: z.record(z.string(), z.unknown()).nullable().optional(),
  collectedData: z.record(z.string(), z.unknown()).nullable().optional(),
  callCost: z.record(z.string(), z.unknown()).nullable().optional(), // Renvoyé undefined par PostgreSQL
  callStartedAt: z.string().nullable().optional(),
  callEndedAt: z.string().nullable().optional(),
  testerFirstName: z.string().nullable().optional(),
  testerLastName: z.string().nullable().optional(),
});

export const AgentTestCallSchema = BaseAgentTestCallSchema;

export const AgentTestCallListItemSchema = BaseAgentTestCallSchema.extend({
  agentName: z.string().nullable().optional(),
  phaseName: z.string().nullable().optional(),
  testerName: z.string().nullable().optional(),
});

export const AgentTestCallListSchema = z.array(AgentTestCallListItemSchema);

// Admin list item schema (includes agent and workspace info for admin view)
export const AdminTestCallListItemSchema = AgentTestCallListItemSchema.extend({
  agentName: z.string().nullable().optional(),
  phaseName: z.string().nullable().optional(),
  workspaceId: z.string().uuid().nullable().optional(),
  workspaceName: z.string().nullable().optional(),
  testDynamicVariables: z.record(z.string(), z.unknown()).nullable().optional(),
});

export const AdminTestCallListSchema = z.array(AdminTestCallListItemSchema);

// Batch refresh request schema
export const BatchRefreshTestCallsSchema = z.object({
  testCallIds: z.array(z.string().uuid()).min(1).max(50),
});

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type TestCallStatus = z.infer<typeof TestCallStatusSchema>;
export type TestResult = z.infer<typeof TestResultSchema>;
export type AgentTestCall = z.infer<typeof AgentTestCallSchema>;
export type AgentTestCallListItem = z.infer<typeof AgentTestCallListItemSchema>;
export type AdminTestCallListItem = z.infer<typeof AdminTestCallListItemSchema>;
export type BatchRefreshTestCalls = z.infer<typeof BatchRefreshTestCallsSchema>;

// Note: TestPatientData is exported from ./agent.ts
